"use client"

import {
  BracketsSquare,
  CaretDown,
  CaretRight,
  ChartLine,
  ChatCircle,
  Check,
  Clock,
  Command,
  Copy,
  Cube,
  Database,
  Eye,
  MagnifyingGlass,
  Plus,
  Share,
  Star,
  TreeStructure,
} from "@phosphor-icons/react"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

// Mock query result data matching the screenshot
const mockQueryResult = {
  rows: [
    {
      timestamp: "5/1/2012, 12:00:00 AM",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 11443,
    },
    {
      timestamp: "5/2/2012, 12:00:00 AM",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 11573,
    },
    {
      timestamp: "5/5/2012, 12:00:00 AM",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 12299,
    },
    {
      timestamp: "5/10/2012, 12:00:00 AM",
      data_id: "bitcoin",
      metric_id: "active_addresses_daily",
      value: 12667,
    },
  ],
}

// Sidebar table items with appropriate icons
const tableItems = [
  { name: "metrics", count: 2, icon: ChartLine },
  { name: "primitives", count: 2, icon: TreeStructure },
  { name: "contracts", count: 349, icon: BracketsSquare },
  { name: "blockchain", count: 115, icon: Cube },
]

function InteractiveQueryEditorDemo() {
  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl"
      style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
    >
      <div className="bg-bg-default overflow-hidden w-[1000px]">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border-default bg-bg-default">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-fg-secondary">Queries</span>
            <CaretRight className="w-3 h-3 text-fg-secondary" />
            <span className="text-fg-default">Bitcoin active_addresses_daily</span>
            <Star className="w-4 h-4 text-fg-secondary" />
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-fg-secondary">Last updated 21 days ago</span>
            <button className="flex items-center gap-1.5 text-fg-secondary hover:text-fg-default">
              <Share className="w-4 h-4" />
              Share
            </button>
            <button className="flex items-center gap-1.5 text-fg-secondary hover:text-fg-default">
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button className="flex items-center gap-1.5 text-fg-secondary hover:text-fg-default">
              <Copy className="w-4 h-4" />
              Clone
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex">
          {/* Left Sidebar */}
          <div className="w-[270px] shrink-0 border-r border-border-default bg-bg-default flex flex-col">
            {/* Tables Tab Header */}
            <div className="h-[56px] px-4 flex items-center border-b border-border-default">
              <button className="flex items-center gap-1.5 text-sm text-fg-default font-medium py-2 border-b-2 border-fg-default -mb-[1px]">
                <Database className="w-3.5 h-3.5" />
                Tables
              </button>
            </div>

            {/* Sidebar Content */}
            <div className="p-4 flex flex-col gap-3">
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 bg-bg-inset border border-border-default rounded-lg">
                <MagnifyingGlass className="w-4 h-4 text-fg-secondary" />
                <span className="text-sm text-fg-secondary">Search</span>
              </div>

              {/* Show internal tables checkbox */}
              <div className="flex items-center gap-2 ml-2.5">
                <div className="w-4 h-4 bg-fg-secondary/20 rounded flex items-center justify-center">
                  <Check className="w-3 h-3 text-fg-default" />
                </div>
                <span className="text-sm text-fg-secondary">Show internal tables</span>
              </div>

              {/* Table Tree */}
              <div className="flex flex-col rounded-lg overflow-hidden border border-border-default">
                {tableItems.map((item, idx) => (
                  <button
                    key={item.name}
                    className={`flex items-center gap-1.5 h-10 px-2 py-2 w-full text-left bg-bg-default hover:bg-neutral-100 dark:hover:bg-neutral-800 outline outline-1 outline-neutral-200 dark:outline-neutral-800 ${
                      idx === 0 ? "rounded-t-lg" : ""
                    } ${idx === tableItems.length - 1 ? "rounded-b-lg" : ""}`}
                  >
                    <div className="flex justify-center items-center h-4 w-4 text-fg-secondary">
                      <CaretRight className="w-4 h-4" />
                    </div>
                    <div className="flex justify-center items-center h-5 w-5 rounded-md bg-neutral-500/10 dark:bg-neutral-500/30 text-fg-secondary">
                      <item.icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-sm text-fg-default flex-1">{item.name}</span>
                    <span className="text-xs text-fg-secondary">{item.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Query Title */}
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-medium text-fg-default">
                Bitcoin active_addresses_daily
              </h2>
              <button className="text-fg-secondary hover:text-fg-default">
                <ChatCircle className="w-5 h-5" />
              </button>
            </div>

            {/* SQL Editor */}
            <div className="mx-6 mb-4 border border-border-default rounded-lg overflow-hidden bg-bg-inset">
              <div className="flex font-mono text-sm">
                {/* Line Numbers */}
                <div className="py-3 px-3 text-right text-fg-tertiary select-none border-r border-border-default bg-bg-default">
                  <div>1</div>
                  <div>2</div>
                  <div>3</div>
                </div>
                {/* Code */}
                <div className="py-3 px-4 flex-1">
                  <div>
                    <span className="text-[#C586C0]">select</span>
                    <span className="text-fg-default"> *</span>
                  </div>
                  <div>
                    <span className="text-[#C586C0]">from</span>
                    <span className="text-fg-default"> metrics</span>
                  </div>
                  <div>
                    <span className="text-[#C586C0]">where</span>
                    <span className="text-fg-default"> data_id = </span>
                    <span className="text-[#CE9178]">&apos;bitcoin&apos;</span>
                    <span className="text-[#C586C0]"> and</span>
                    <span className="text-fg-default"> metric_id = </span>
                    <span className="text-[#CE9178]">&apos;active_addresses_daily&apos;</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Editor Footer */}
            <div className="flex items-center justify-between px-6 pb-4 text-sm">
              <div className="flex items-center gap-1 text-fg-secondary">
                <span>Press</span>
                <span className="px-1.5 py-0.5 bg-bg-inset border border-border-default rounded text-xs font-medium">
                  <Command className="w-3 h-3 inline" />I
                </span>
                <span>for AI suggestions</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-fg-secondary">Estimated usage 898.29 MB</span>
                <button className="flex items-center gap-1 text-fg-secondary hover:text-fg-default">
                  <Clock className="w-4 h-4" />
                  Add refresh
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
                  Run query
                </button>
              </div>
            </div>

            {/* Status Row */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-border-default">
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4" style={{ color: "#1AFFAB" }} />
                <span className="text-fg-default font-medium">Success</span>
                <span className="text-fg-secondary">21 days ago</span>
                <CaretDown className="w-3 h-3 text-fg-secondary" />
              </div>
              <div className="flex items-center gap-1 text-sm text-fg-secondary">
                <Clock className="w-4 h-4" />
                Run history
              </div>
            </div>

            {/* Results Section */}
            <div className="border-t border-border-default">
              {/* Tabs */}
              <div className="flex items-center gap-4 px-6 py-2 border-b border-border-default">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-bg-inset border border-border-default rounded-lg">
                  <span className="text-sm text-fg-default">Table</span>
                  <span className="text-fg-secondary">...</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-fg-secondary">
                  <span>Chart</span>
                  <Plus className="w-3 h-3" />
                </div>
              </div>

              {/* Results Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-default bg-bg-default">
                      <th className="px-6 py-3 text-left text-sm font-normal text-fg-default">
                        <div className="flex items-center gap-1">
                          timestamp
                          <CaretDown className="w-3 h-3 text-fg-secondary" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-normal text-fg-default">
                        <div className="flex items-center gap-1">
                          data_id
                          <CaretDown className="w-3 h-3 text-fg-secondary" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-normal text-fg-default">
                        <div className="flex items-center gap-1">
                          metric_id
                          <CaretDown className="w-3 h-3 text-fg-secondary" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-normal text-fg-default">
                        <div className="flex items-center gap-1">
                          value
                          <CaretDown className="w-3 h-3 text-fg-secondary" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockQueryResult.rows.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-border-default hover:bg-bg-inset transition-colors"
                      >
                        <td className="px-6 py-3 text-sm text-fg-default">{row.timestamp}</td>
                        <td className="px-6 py-3 text-sm text-fg-default">{row.data_id}</td>
                        <td className="px-6 py-3 text-sm text-fg-default">{row.metric_id}</td>
                        <td className="px-6 py-3 text-sm text-fg-default">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InteractiveQueryEditorDemo
